import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiText } from '../api';

// ─── Cluster ─────────────────────────────────────────────────────────────

export interface ClusterTenantOverview {
  code: string;
  name: string;
  database_name: string;
  active: boolean;
  created_at: string;
  db_size_bytes: number | null;
  table_count: number | null;
  error: string | null;
}

export interface ClusterOverviewResponse {
  cluster: { size_bytes: number; database_count: number };
  tenants: ClusterTenantOverview[];
}

export function useClusterOverview() {
  return useQuery({
    queryKey: ['cluster', 'overview'],
    queryFn: () => api<ClusterOverviewResponse>('/admin/cluster/overview'),
    refetchInterval: 30_000,
  });
}

export interface ClusterHealthResponse {
  connections: { active: number; max: number };
  databases: Array<{ name: string; size_bytes: number }>;
}

export function useClusterHealth() {
  return useQuery({
    queryKey: ['cluster', 'health'],
    queryFn: () => api<ClusterHealthResponse>('/admin/cluster/health'),
    refetchInterval: 30_000,
  });
}

// ─── Tenants (platform_db.core.tenants) ─────────────────────────────────

export interface TenantRow {
  id: string;
  code: string;
  name: string;
  database_name: string;
  active: boolean;
  created_at: string;
  updated_at: string;
  config: Record<string, unknown>;
}

export function useTenants() {
  return useQuery({
    queryKey: ['tenants'],
    queryFn: () => api<{ data: TenantRow[] }>('/tenants'),
  });
}

// ─── Integrations-Konfiguration ─────────────────────────────────────────────

export interface IntegrationRow {
  id: string;
  code: string;
  type: 'wms' | 'erp' | 'carrier' | 'other';
  vendor: string;
  active: boolean;
  config: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface EndpointRow {
  id: string;
  integration_id: string;
  environment: 'sandbox' | 'live';
  base_url: string;
  auth_method: 'oauth2' | 'apikey' | 'hmac' | 'basic' | 'mtls';
  config: Record<string, unknown>;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CredentialRow {
  id: string;
  secrets_manager_ref: string;
  secret_version: string | null;
  rotation_due: string | null;
  active: boolean;
  created_at: string;
  rotated_at: string | null;
  rotated_by: string | null;
}

export interface WebhookSubscriptionRow {
  id: string;
  integration_id: string;
  environment: 'sandbox' | 'live';
  event_type: string;
  receiver_url: string;
  signature_method: 'hmac-sha256' | 'mtls' | 'bearer' | 'none' | null;
  signature_secret_ref: string | null;
  status: 'active' | 'paused' | 'rotating' | 'retired';
  last_received_at: string | null;
  last_error_at: string | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
}

export function useIntegrations(tenantCode: string | undefined) {
  return useQuery({
    queryKey: ['integrations', tenantCode],
    queryFn: () => api<{ data: IntegrationRow[] }>(`/tenants/${tenantCode}/integrations`),
    enabled: !!tenantCode,
  });
}

export function useIntegrationEndpoints(tenantCode: string | undefined, integrationId: string | undefined) {
  return useQuery({
    queryKey: ['integration-endpoints', tenantCode, integrationId],
    queryFn: () =>
      api<{ data: EndpointRow[] }>(`/tenants/${tenantCode}/integrations/${integrationId}/endpoints`),
    enabled: !!(tenantCode && integrationId),
  });
}

export function useIntegrationCredentials(
  tenantCode: string | undefined,
  integrationId: string | undefined,
  endpointId: string | undefined,
) {
  return useQuery({
    queryKey: ['integration-credentials', tenantCode, integrationId, endpointId],
    queryFn: () =>
      api<{ data: CredentialRow[] }>(
        `/tenants/${tenantCode}/integrations/${integrationId}/endpoints/${endpointId}/credentials`,
      ),
    enabled: !!(tenantCode && integrationId && endpointId),
  });
}

export function useWebhookSubscriptions(tenantCode: string | undefined, integrationId: string | undefined) {
  return useQuery({
    queryKey: ['webhook-subscriptions', tenantCode, integrationId],
    queryFn: () =>
      api<{ data: WebhookSubscriptionRow[] }>(
        `/tenants/${tenantCode}/integrations/${integrationId}/webhook-subscriptions`,
      ),
    enabled: !!(tenantCode && integrationId),
  });
}

export function useIntegrationMutations(tenantCode: string | undefined) {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['integrations', tenantCode] });
    void qc.invalidateQueries({ queryKey: ['integration-endpoints', tenantCode] });
    void qc.invalidateQueries({ queryKey: ['integration-credentials', tenantCode] });
    void qc.invalidateQueries({ queryKey: ['webhook-subscriptions', tenantCode] });
  };

  return {
    updateIntegration: useMutation({
      mutationFn: (args: { integrationId: string; patch: Partial<Pick<IntegrationRow, 'active' | 'config'>> }) =>
        api<IntegrationRow>(`/tenants/${tenantCode}/integrations/${args.integrationId}`, {
          method: 'PATCH',
          body: JSON.stringify(args.patch),
        }),
      onSuccess: invalidate,
    }),
    createEndpoint: useMutation({
      mutationFn: (args: {
        integrationId: string;
        payload: {
          environment: 'sandbox' | 'live';
          base_url: string;
          auth_method: EndpointRow['auth_method'];
          config: Record<string, unknown>;
          active: boolean;
        };
      }) =>
        api<EndpointRow>(`/tenants/${tenantCode}/integrations/${args.integrationId}/endpoints`, {
          method: 'POST',
          body: JSON.stringify(args.payload),
        }),
      onSuccess: invalidate,
    }),
    updateEndpoint: useMutation({
      mutationFn: (args: {
        integrationId: string;
        endpointId: string;
        patch: Partial<Pick<EndpointRow, 'base_url' | 'auth_method' | 'config' | 'active'>>;
      }) =>
        api<EndpointRow>(
          `/tenants/${tenantCode}/integrations/${args.integrationId}/endpoints/${args.endpointId}`,
          {
            method: 'PATCH',
            body: JSON.stringify(args.patch),
          },
        ),
      onSuccess: invalidate,
    }),
    recordCredentialRef: useMutation({
      mutationFn: (args: {
        integrationId: string;
        endpointId: string;
        payload: {
          secrets_manager_ref: string;
          secret_version?: string | null;
          rotation_due?: string | null;
          active: boolean;
          reason?: string;
        };
      }) =>
        api<CredentialRow>(
          `/tenants/${tenantCode}/integrations/${args.integrationId}/endpoints/${args.endpointId}/credentials/reference`,
          {
            method: 'POST',
            body: JSON.stringify(args.payload),
          },
        ),
      onSuccess: invalidate,
    }),
    createWebhook: useMutation({
      mutationFn: (args: {
        integrationId: string;
        payload: {
          environment: WebhookSubscriptionRow['environment'];
          event_type: string;
          receiver_url: string;
          signature_method?: WebhookSubscriptionRow['signature_method'];
          signature_secret_ref?: string | null;
          status: WebhookSubscriptionRow['status'];
        };
      }) =>
        api<WebhookSubscriptionRow>(
          `/tenants/${tenantCode}/integrations/${args.integrationId}/webhook-subscriptions`,
          {
            method: 'POST',
            body: JSON.stringify(args.payload),
          },
        ),
      onSuccess: invalidate,
    }),
    updateWebhook: useMutation({
      mutationFn: (args: {
        integrationId: string;
        webhookId: string;
        patch: Partial<
          Pick<
            WebhookSubscriptionRow,
            'environment' | 'receiver_url' | 'signature_method' | 'signature_secret_ref' | 'status'
          >
        >;
      }) =>
        api<WebhookSubscriptionRow>(
          `/tenants/${tenantCode}/integrations/${args.integrationId}/webhook-subscriptions/${args.webhookId}`,
          {
            method: 'PATCH',
            body: JSON.stringify(args.patch),
          },
        ),
      onSuccess: invalidate,
    }),
  };
}

// ─── DB-Health pro Tenant ───────────────────────────────────────────────

export interface DbHealthResponse {
  database_name: string;
  size_bytes: number;
  connections: number;
  oldest_active_xact: string | null;
}

export function useDbHealth(tenantCode: string | undefined) {
  return useQuery({
    queryKey: ['db-health', tenantCode],
    queryFn: () => api<DbHealthResponse>(`/tenants/${tenantCode}/db/health`),
    enabled: !!tenantCode,
    refetchInterval: 30_000,
  });
}

// ─── DB-Schemas + Tables ────────────────────────────────────────────────

export interface SchemaRow {
  schema: string;
  table_count: number;
  size_bytes: number;
}

export function useDbSchemas(tenantCode: string | undefined) {
  return useQuery({
    queryKey: ['db-schemas', tenantCode],
    queryFn: () => api<{ schemas: SchemaRow[] }>(`/tenants/${tenantCode}/db/schemas`),
    enabled: !!tenantCode,
  });
}

export interface TableRow {
  schema: string;
  table: string;
  row_estimate: number;
  total_size_bytes: number;
  indexes_size_bytes: number;
}

export function useDbTables(tenantCode: string | undefined, schema?: string) {
  return useQuery({
    queryKey: ['db-tables', tenantCode, schema ?? 'all'],
    queryFn: () => {
      const qs = schema ? `?schema=${encodeURIComponent(schema)}` : '';
      return api<{ tables: TableRow[] }>(`/tenants/${tenantCode}/db/tables${qs}`);
    },
    enabled: !!tenantCode,
  });
}

// ─── Table Detail ───────────────────────────────────────────────────────

export interface TableDetailResponse {
  schema: string;
  table: string;
  columns: Array<{
    name: string;
    data_type: string;
    nullable: boolean;
    default: string | null;
    position: number;
    is_pii: boolean;
    is_large_json?: boolean;
  }>;
  indexes: Array<{ name: string; unique: boolean; primary: boolean; definition: string }>;
  foreign_keys: Array<{
    name: string;
    column: string;
    references: { schema: string; table: string; column: string };
    on_delete: string;
  }>;
  stats: { row_estimate: number; total_size_bytes: number; indexes_size_bytes: number };
}

export function useTableDetail(tenantCode: string | undefined, schema: string | undefined, table: string | undefined) {
  return useQuery({
    queryKey: ['table-detail', tenantCode, schema, table],
    queryFn: () =>
      api<TableDetailResponse>(`/tenants/${tenantCode}/db/tables/${schema}/${table}`),
    enabled: !!(tenantCode && schema && table),
  });
}

export interface SampleResponse {
  schema: string;
  table: string;
  limit: number;
  offset: number;
  has_more: boolean;
  order_by: string | null;
  order_dir: 'asc' | 'desc';
  search: string | null;
  row_count: number;
  columns: Array<{ name: string; redacted: boolean; omitted?: boolean; virtual?: boolean }>;
  omitted_columns: Array<{ name: string; reason: string }>;
  rows: Array<Record<string, unknown>>;
}

export function fetchSample(
  tenantCode: string,
  schema: string,
  table: string,
  options: {
    includePayload?: boolean;
    limit?: number;
    offset?: number;
    orderBy?: string;
    orderDir?: 'asc' | 'desc';
    q?: string;
  } = {},
) {
  const qs = new URLSearchParams();
  qs.set('limit', String(options.limit ?? 50));
  qs.set('offset', String(options.offset ?? 0));
  qs.set('order_dir', options.orderDir ?? 'desc');
  if (options.orderBy) qs.set('order_by', options.orderBy);
  if (options.q) qs.set('q', options.q);
  if (options.includePayload) qs.set('expand_payload', 'true');
  return api<SampleResponse>(
    `/tenants/${tenantCode}/db/tables/${schema}/${table}/sample?${qs.toString()}`,
  );
}

export interface SourceSchemaCatalogRow {
  source_schema: string;
  source_table: string;
  payload_column: string;
  field_path: string;
  value_types: string[];
  occurrence_count: number;
  last_scan_count: number;
  sample_value: unknown;
  status: 'new' | 'known' | 'missing' | 'type_changed';
  first_seen_at: string;
  last_seen_at: string;
  last_scanned_at: string;
}

export function useSourceSchemaCatalog(
  tenantCode: string | undefined,
  sourceSchema: string | undefined,
  sourceTable: string | undefined,
) {
  return useQuery({
    queryKey: ['source-schema-catalog', tenantCode, sourceSchema, sourceTable],
    queryFn: () => {
      const qs = new URLSearchParams();
      qs.set('source_schema', sourceSchema ?? 'sources');
      if (sourceTable) qs.set('source_table', sourceTable);
      return api<{ data: SourceSchemaCatalogRow[] }>(
        `/tenants/${tenantCode}/db/source-schema-catalog?${qs.toString()}`,
      );
    },
    enabled: !!(tenantCode && sourceSchema && sourceTable),
  });
}

// ─── Operations ─────────────────────────────────────────────────────────

export interface SyncStateRow {
  source_system: string;
  entity: string;
  status: string;
  last_full_sync_at: string | null;
  last_delta_sync_at: string | null;
  last_error: string | null;
  last_error_at: string | null;
  rows_processed: number;
  rows_created: number;
  rows_updated: number;
  rows_skipped: number;
  started_at: string | null;
  finished_at: string | null;
  updated_at: string;
}

export function useSyncState(tenantCode: string | undefined) {
  return useQuery({
    queryKey: ['sync-state', tenantCode],
    queryFn: () => api<{ data: SyncStateRow[] }>(`/tenants/${tenantCode}/sync-state`),
    enabled: !!tenantCode,
    refetchInterval: 30_000,
  });
}

export interface SyncStrategyDefinitionRow {
  code: string;
  label: string;
  description: string;
  best_for: string;
  api_load_notes: string;
  cursor_description: string;
  large_table_safe: boolean;
  default_min_frequency_minutes: number;
  sort_order: number;
}

export function useSyncStrategies(tenantCode: string | undefined) {
  return useQuery({
    queryKey: ['sync-strategies', tenantCode],
    queryFn: () => api<{ data: SyncStrategyDefinitionRow[] }>(`/tenants/${tenantCode}/sync-strategies`),
    enabled: !!tenantCode,
    staleTime: 60_000,
  });
}

export interface SyncConfigRow {
  id: string;
  integration_id: string;
  integration_code: string;
  source_system: string;
  environment: 'sandbox' | 'live';
  entity: string;
  enabled: boolean;
  strategy: string;
  frequency_minutes: number;
  safety_lag_minutes: number;
  rolling_window_days: number;
  page_size: number;
  max_requests_per_minute: number;
  cooldown_ms_between_pages: number;
  max_runtime_seconds: number;
  max_parallel_runs: number;
  priority: number;
  cursor: Record<string, unknown>;
  next_run_at: string | null;
  last_run_at: string | null;
  last_success_at: string | null;
  last_error_at: string | null;
  last_error: string | null;
  notes: string;
  created_at: string;
  updated_at: string;
  entity_display_name: string | null;
  entity_description: string | null;
  source_schema: string | null;
  source_table: string | null;
  default_strategy: string | null;
  recommended_min_frequency_minutes: number | null;
  large_table: boolean | null;
  implemented: boolean | null;
  implementation_notes: string | null;
  executable_strategies: string[];
  strategy_executable: boolean;
  strategy_label: string | null;
  strategy_description: string | null;
  last_run: {
    id: string;
    status: string;
    trigger_type: string;
    started_at: string;
    finished_at: string | null;
    rows_processed: number;
    rows_created: number;
    rows_updated: number;
    rows_skipped: number;
    error_message: string | null;
  } | null;
}

export type SyncConfigPatch = Partial<
  Pick<
    SyncConfigRow,
    | 'enabled'
    | 'strategy'
    | 'frequency_minutes'
    | 'safety_lag_minutes'
    | 'rolling_window_days'
    | 'page_size'
    | 'max_requests_per_minute'
    | 'cooldown_ms_between_pages'
    | 'max_runtime_seconds'
    | 'max_parallel_runs'
    | 'priority'
    | 'notes'
  >
>;

export function useSyncConfigs(tenantCode: string | undefined) {
  return useQuery({
    queryKey: ['sync-configs', tenantCode],
    queryFn: () => api<{ data: SyncConfigRow[] }>(`/tenants/${tenantCode}/sync-configs`),
    enabled: !!tenantCode,
    refetchInterval: 10_000,
  });
}

export interface SyncRunRow {
  id: string;
  sync_config_id: string | null;
  integration_id: string | null;
  source_system: string;
  environment: string;
  entity: string;
  strategy: string;
  trigger_type: string;
  status: string;
  started_at: string;
  finished_at: string | null;
  rows_processed: number | string;
  rows_created: number | string;
  rows_updated: number | string;
  rows_skipped: number | string;
  request_count: number;
  error_message: string | null;
  trace_id: string | null;
  created_at: string;
  duration_ms: number | string | null;
}

export function useSyncRuns(
  tenantCode: string | undefined,
  filter: {
    status?: string;
    source_system?: string;
    entity?: string;
    environment?: string;
    limit?: number;
    offset?: number;
  } = {},
) {
  return useQuery({
    queryKey: ['sync-runs', tenantCode, filter],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (filter.status) qs.set('status', filter.status);
      if (filter.source_system) qs.set('source_system', filter.source_system);
      if (filter.entity) qs.set('entity', filter.entity);
      if (filter.environment) qs.set('environment', filter.environment);
      qs.set('limit', String(filter.limit ?? 100));
      qs.set('offset', String(filter.offset ?? 0));
      return api<{ total: number; limit: number; offset: number; data: SyncRunRow[] }>(
        `/tenants/${tenantCode}/sync-runs?${qs.toString()}`,
      );
    },
    enabled: !!tenantCode,
    refetchInterval: 2_000,
    refetchIntervalInBackground: true,
    staleTime: 1_000,
  });
}

export function fetchSyncRunExport(tenantCode: string, runId: string): Promise<string> {
  return apiText(`/tenants/${tenantCode}/sync-runs/${runId}/export.txt`);
}

export function useSyncConfigMutations(tenantCode: string | undefined) {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['sync-configs', tenantCode] });
    void qc.invalidateQueries({ queryKey: ['sync-state', tenantCode] });
  };
  return {
    updateConfig: useMutation({
      mutationFn: (args: { configId: string; patch: SyncConfigPatch }) =>
        api<SyncConfigRow>(`/tenants/${tenantCode}/sync-configs/${args.configId}`, {
          method: 'PATCH',
          body: JSON.stringify(args.patch),
        }),
      onSuccess: invalidate,
    }),
    runNow: useMutation({
      mutationFn: (configId: string) =>
        api<SyncConfigRow>(`/tenants/${tenantCode}/sync-configs/${configId}/run-now`, {
          method: 'POST',
        }),
      onSuccess: invalidate,
    }),
  };
}

export interface OutboxSummaryRow {
  target_system: string;
  status: string;
  count: number;
}
export function useOutboxSummary(tenantCode: string | undefined) {
  return useQuery({
    queryKey: ['outbox-summary', tenantCode],
    queryFn: () =>
      api<{ data: OutboxSummaryRow[] }>(`/tenants/${tenantCode}/outbox/summary`),
    enabled: !!tenantCode,
    refetchInterval: 15_000,
  });
}

export interface OutboxRow {
  id: string;
  source_tool: string;
  target_system: string;
  event_type: string;
  idempotency_key: string;
  status: string;
  attempts: number;
  last_error: string | null;
  next_retry_at: string | null;
  http_status: number | null;
  created_at: string;
  sent_at: string | null;
  trace_id: string | null;
}
export function useOutbox(
  tenantCode: string | undefined,
  filter: { status?: string; target?: string; limit?: number; offset?: number } = {},
) {
  return useQuery({
    queryKey: ['outbox', tenantCode, filter],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (filter.status) qs.set('status', filter.status);
      if (filter.target) qs.set('target', filter.target);
      qs.set('limit', String(filter.limit ?? 50));
      qs.set('offset', String(filter.offset ?? 0));
      return api<{ total: number; limit: number; offset: number; data: OutboxRow[] }>(
        `/tenants/${tenantCode}/outbox?${qs.toString()}`,
      );
    },
    enabled: !!tenantCode,
  });
}

export interface WebhookEventRow {
  id: string;
  source_system: string;
  event_type: string;
  external_event_id: string | null;
  signature_valid: boolean | null;
  received_at: string;
  processed_at: string | null;
  status: string;
  error_message: string | null;
  attempts: number;
  trace_id: string | null;
}
export function useWebhookEvents(
  tenantCode: string | undefined,
  filter: { status?: string; limit?: number; offset?: number } = {},
) {
  return useQuery({
    queryKey: ['webhook-events', tenantCode, filter],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (filter.status) qs.set('status', filter.status);
      qs.set('limit', String(filter.limit ?? 50));
      qs.set('offset', String(filter.offset ?? 0));
      return api<{ total: number; limit: number; offset: number; data: WebhookEventRow[] }>(
        `/tenants/${tenantCode}/webhook-events?${qs.toString()}`,
      );
    },
    enabled: !!tenantCode,
    refetchInterval: 2_000,
    refetchIntervalInBackground: true,
    staleTime: 1_000,
  });
}

// ─── Global Audit ───────────────────────────────────────────────────────

export interface AuditGlobalRow {
  id: string;
  occurred_at: string;
  actor_type: string;
  actor_ref: string | null;
  tenant_code: string | null;
  action: string;
  target_kind: string | null;
  target_ref: string | null;
  metadata: Record<string, unknown> | null;
  trace_id: string | null;
}
export function useAuditGlobal(filter: {
  action?: string;
  actor?: string;
  tenant_code?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: ['audit-global', filter],
    queryFn: () => {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(filter)) {
        if (v !== undefined && v !== '') qs.set(k, String(v));
      }
      return api<{ total: number; limit: number; offset: number; data: AuditGlobalRow[] }>(
        `/audit/global?${qs.toString()}`,
      );
    },
  });
}

// External API Access

export interface ExternalApiClientRow {
  id: string;
  code: string;
  display_name: string;
  processor_name: string | null;
  contact_ref: string | null;
  purpose: string;
  avv_reference: string | null;
  reidentification_prohibited: boolean;
  ip_allowlist: string[];
  active: boolean;
  revoked_at: string | null;
  created_at: string;
  updated_at: string;
  updated_by: string | null;
}

export interface ExternalApiEndpointRow {
  id: string;
  client_id: string;
  http_method: string;
  path: string;
  display_name: string;
  description: string;
  data_contract_version: string;
  max_days_per_request: number;
  max_rows_per_request: number;
  rate_limit_per_minute: number;
  https_required: boolean;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ExternalApiTokenRow {
  id: string;
  client_id: string;
  label: string;
  token_prefix: string;
  active: boolean;
  created_at: string;
  created_by: string | null;
  expires_at: string | null;
  rotation_due: string | null;
  rotated_at: string | null;
  revoked_at: string | null;
  revoked_by: string | null;
  last_used_at: string | null;
  last_used_ip: string | null;
}

export interface ExternalApiAccessLogRow {
  id: string;
  request_id: string;
  client_id: string | null;
  client_code: string | null;
  endpoint_id: string | null;
  token_id: string | null;
  token_prefix: string | null;
  http_method: string;
  path: string;
  status_code: number;
  allowed: boolean;
  decision: string;
  remote_ip: string | null;
  from_date: string | null;
  to_date: string | null;
  query_params: Record<string, unknown>;
  row_count: number | null;
  duration_ms: number | null;
  error_code: string | null;
  occurred_at: string;
}

export interface ExternalApiFieldCatalogRow {
  endpoint_path: string;
  field_name: string;
  classification: string;
  source_reference: string;
  purpose: string;
  included: boolean;
}

export interface ExternalAccessTenant {
  tenant_code: string;
  clients: ExternalApiClientRow[];
  endpoints: ExternalApiEndpointRow[];
  tokens: ExternalApiTokenRow[];
  recent_logs: ExternalApiAccessLogRow[];
  field_catalog: ExternalApiFieldCatalogRow[];
  error: string | null;
}

export interface ExternalAccessSnapshot {
  tenant_code: string;
  endpoint: ExternalApiEndpointRow;
  source: {
    schema: string;
    name: string;
    type: 'view' | 'table';
    reference: string;
  };
  limit: number;
  order_by: string;
  generated_at: string;
  field_catalog: Array<{
    field_name: string;
    classification: string;
    source_reference: string;
    purpose: string;
  }>;
  columns: Array<{ name: string }>;
  row_count: number;
  rows: Array<Record<string, unknown>>;
}

export function useExternalAccessOverview(filter: { tenantCode?: string; limit?: number } = {}) {
  return useQuery({
    queryKey: ['external-access', filter],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (filter.tenantCode) qs.set('tenant_code', filter.tenantCode);
      qs.set('limit', String(filter.limit ?? 100));
      return api<{ data: ExternalAccessTenant[] }>(`/external-access/overview?${qs.toString()}`);
    },
    refetchInterval: 15_000,
  });
}

export function useExternalAccessSnapshot(
  tenantCode: string | undefined,
  endpointPath: string | null,
  limit = 50,
) {
  return useQuery({
    queryKey: ['external-access-snapshot', tenantCode, endpointPath, limit],
    queryFn: () => {
      const qs = new URLSearchParams();
      qs.set('path', endpointPath ?? '');
      qs.set('limit', String(limit));
      return api<ExternalAccessSnapshot>(
        `/tenants/${tenantCode}/external-access/snapshots?${qs.toString()}`,
      );
    },
    enabled: !!(tenantCode && endpointPath),
  });
}

export function useExternalAccessMutations() {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['external-access'] });
  };

  return {
    upsertDefaultClient: useMutation({
      mutationFn: (args: {
        tenantCode: string;
        payload: {
          active: boolean;
          processorName?: string | null;
          contactRef?: string | null;
          avvReference?: string | null;
          ipAllowlist: string[];
        };
      }) =>
        api<{ client: ExternalApiClientRow }>(
          `/tenants/${args.tenantCode}/external-access/default-sales-analysis`,
          {
            method: 'POST',
            body: JSON.stringify(args.payload),
          },
        ),
      onSuccess: invalidate,
    }),
    updateClient: useMutation({
      mutationFn: (args: {
        tenantCode: string;
        clientId: string;
        patch: Partial<{
          active: boolean;
          revoked: boolean;
          processorName: string | null;
          contactRef: string | null;
          avvReference: string | null;
          reidentificationProhibited: boolean;
          ipAllowlist: string[];
        }>;
      }) =>
        api<{ client: ExternalApiClientRow }>(
          `/tenants/${args.tenantCode}/external-access/clients/${args.clientId}`,
          {
            method: 'PATCH',
            body: JSON.stringify(args.patch),
          },
        ),
      onSuccess: invalidate,
    }),
    createToken: useMutation({
      mutationFn: (args: {
        tenantCode: string;
        clientId: string;
        payload: { label?: string; expiresAt?: string | null; rotationDue?: string | null };
      }) =>
        api<{ token: ExternalApiTokenRow; token_value: string }>(
          `/tenants/${args.tenantCode}/external-access/clients/${args.clientId}/tokens`,
          {
            method: 'POST',
            body: JSON.stringify(args.payload),
          },
        ),
      onSuccess: invalidate,
    }),
    revokeToken: useMutation({
      mutationFn: (args: { tenantCode: string; tokenId: string }) =>
        api<{ token: ExternalApiTokenRow }>(
          `/tenants/${args.tenantCode}/external-access/tokens/${args.tokenId}`,
          {
            method: 'PATCH',
            body: JSON.stringify({ revoked: true }),
          },
        ),
      onSuccess: invalidate,
    }),
  };
}
